import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, onMounted, ref, watch } from "vue";

import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { IPaymentMethod, IServicePaymentMethods } from "@/data/model/Customer";
import { BuyerRequirement } from "@/data/model/Enums";

import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";

import { operatorEvents } from "@/operator/events/operatorEvents";
import { PayContext } from "@/operator/movedComponents/PayContext";

import { useBillStore } from "./bill";
import { useCartStore } from "./cart";
import { useOfferStore } from "./offer";

export const useCartPaymentStore = defineStore("cartPayment", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const payContext = new PayContext(0);

    const cart = useCartStore();
    const offer = useOfferStore();
    const bill = useBillStore();

    // state
    const paymentService = computed(() => offer.orderService);
    const isProcessingPayment = ref(false);
    const isLoadingPaymentMethods = ref(false);
    const availablePaymentMethods = ref<IServicePaymentMethods | null>(null);
    const isProcessingPaymentDevicePayment = ref(false);
    const buyerEmail = ref<string | undefined>();

    // getters
    const operatorPaymentMethods = computed(() => availablePaymentMethods.value?.paymentMethods ?? []);
    const operatorQuickPaymentMethods = computed(() => availablePaymentMethods.value?.quickPaymentMethods ?? []);
    const buyerRequirement = computed(() => offer.orderService?.useCase?.buyerRequirement ?? BuyerRequirement.None);
    const isEmailRequired = computed(
        () =>
            buyerRequirement.value == BuyerRequirement.Email ||
            buyerRequirement.value == BuyerRequirement.SignedUp ||
            buyerRequirement.value == BuyerRequirement.EmailConfirmed
    );

    // actions
    async function payImmediatelly(
        consumption: number,
        tip: number,
        paymentMethod: IPaymentMethod,
        anonymousUserEmail?: string,
        endUserEmail?: string
    ): Promise<boolean> {
        try {
            isProcessingPayment.value = true;
            isProcessingPaymentDevicePayment.value = paymentMethod.isPaymentDevice;

            if (!paymentService.value) throw translations.pay.noActivePaymentService;

            payContext.setPaymentMethod(paymentMethod);

            // todo: verify order service passing as param - maybe only id is enough
            const onGoBackToFulfillmentTimeView = () => {
                operatorEvents.payContext.goBackToPreorderTimeSelectionRequested.dispatch();
            };
            await payContext.pay(
                paymentService.value,
                cart.currentCart!.hash,
                true,
                consumption,
                tip,
                undefined,
                undefined,
                anonymousUserEmail,
                endUserEmail,
                onGoBackToFulfillmentTimeView
            );
            return true;
        } catch {
            return false;
        } finally {
            isProcessingPayment.value = false;
            isProcessingPaymentDevicePayment.value = false;
        }
    }

    async function loadAvailablePaymentMethods() {
        try {
            isLoadingPaymentMethods.value = true;
            availablePaymentMethods.value = null;

            if (!paymentService.value) {
                return;
            }

            const servicePaymentMethods = await customerDao.getServicePaymentMethods(paymentService.value.id);
            availablePaymentMethods.value = servicePaymentMethods;
        } catch (ex) {
            logError(ex);
        } finally {
            isLoadingPaymentMethods.value = false;
        }
    }

    watch(paymentService, () => {
        loadAvailablePaymentMethods();
        buyerEmail.value = undefined;
    });

    onMounted(() => {
        loadAvailablePaymentMethods();
        buyerEmail.value = undefined;
    });

    payContext.onCheckoutPayLaterCartSuccess.add(async () => await onCheckoutCartSuccess());
    payContext.onCheckoutPayImmediatellyCartSuccess.add(async () => await onCheckoutCartSuccess());

    async function onCheckoutCartSuccess() {
        console.log("checkout cart success");
        buyerEmail.value = undefined;

        // todo: ts-1064 - this is a fallback - we should get websocket notification, but this should work just in case
        await bill.loadCurrentBill();

        cart.resetCart();
    }

    return {
        // state
        paymentService,
        isProcessingPayment,
        isLoadingPaymentMethods,
        isProcessingPaymentDevicePayment,
        buyerEmail,

        // getters
        operatorPaymentMethods,
        operatorQuickPaymentMethods,
        isEmailRequired,

        // actions
        payImmediatelly,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCartPaymentStore, import.meta.hot));
}
